import { liveWindowAt } from '@apm/contracts';
import type { PlaylistResponse } from '@apm/contracts';

import type { EpgRow } from '../epg.js';

/**
 * 프리뷰의 **주소와 시작점** (v32 · `docs/63` §5.3 · 절대 규칙 81).
 *
 * 편성표 응답(`LineupItem`)에는 `videoUrl` 이 없다 — 편성표는 재생 경로가 아니다(규칙 68).
 * 그래서 홈이 서버에 새로 묻는 것이 이것 하나다: 대상 채널의 `GET …/playlist` 에서
 * **`items[0].videoUrl` 과 `resume.positionSec` 둘만 읽고 응답을 버린다.** `playlistId` 는
 * 어디에도 넘기지 않는다 — 요청마다 새것이라 어떤 재생과도 이어지지 않는 유령이다(규칙 68).
 * 그 GET 은 읽기이고 행을 남기지 않는다(`docs/63` A8).
 *
 * ONAIR 채널은 묻지 않는다 — `channel.onair.streamUrl` 이 곧 프리뷰다(라이브 · 시작점 없음).
 *
 * 실패는 `null` 이고 그때 masthead 는 포스터다. 어떤 실패도 홈을 막지 않는다.
 */
export interface PreviewSource {
  readonly url: string;
  readonly startSec: number;
}

export async function fetchPreviewSource(
  row: EpgRow,
  options: {
    serverUrl: string;
    deviceId?: string | undefined;
    token: string | null;
    signal?: AbortSignal;
    /** 홈쇼핑 — 「지금」 카드의 상품. 프리뷰와 카드가 같은 것을 가리키게(비로그인의 기기 기억도) */
    startFromProductId?: string | null;
  },
): Promise<PreviewSource | null> {
  if (row.channel.delivery === 'onair') {
    const url = row.channel.onair?.streamUrl ?? null;
    return url === null ? null : { url, startSec: 0 };
  }
  /*
   * ★ v33 — **라이브가 도는 동안 그 채널의 프리뷰는 포스터다** (`docs/67` D19 · 규칙 82).
   *
   * 첫 녹화물을 틀면 **거짓말이다** — 지금 그 채널에서 나가는 것이 아니다. 그렇다고 라이브를
   * 틀 수도 없다: **홈은 주소를 애초에 받지 않는다**(조망에는 `streamUrl` 이 없다 · 규칙 68 의
   * 단서). 그래서 `null` 이고 masthead 는 포스터다 — **구조적으로 그럴 수밖에 없다.**
   */
  if (liveWindowAt(row.liveEvents, Date.now()).current !== null) return null;
  const base = options.serverUrl.replace(/\/+$/, '');
  const params = new URLSearchParams();
  if (options.deviceId !== undefined && options.deviceId !== '')
    params.set('deviceId', options.deviceId);
  if (options.startFromProductId) params.set('startFromProductId', options.startFromProductId);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  try {
    const response = await fetch(
      `${base}/v1/channels/${encodeURIComponent(row.channel.channelId)}/playlist${query}`,
      {
        ...(options.signal ? { signal: options.signal } : {}),
        headers: options.token === null ? {} : { Authorization: `Bearer ${options.token}` },
      },
    );
    if (!response.ok) return null;
    const playlist = (await response.json()) as PlaylistResponse;
    const first = playlist.items[0];
    if (first === undefined || first.videoUrl === '') return null;
    // 서버가 이어볼 회차를 맨 앞으로 돌려 두므로(`rotateToProduct`) 지점은 첫 항목의 것이다.
    const startSec =
      playlist.resume !== null && playlist.resume.productId === first.productId
        ? Math.max(0, playlist.resume.positionSec)
        : 0;
    return { url: first.videoUrl, startSec };
  } catch {
    return null;
  }
}
